//
// Copyright 2026 DXOS.org
//

// @import-as-namespace

import * as Schema from 'effect/Schema';

import { Annotation, DXN, Format, Obj, Ref, Type } from '@dxos/echo';

import * as Issue from './Issue.ts';
import * as Repo from './Repo.ts';
// Lazily referenced below: `Task` imports this module, so an eager `Task.Task` read here would see it uninitialized.
import * as Task from './Task.ts';

export const State = Schema.Literals(['open', 'closed', 'merged', 'draft']);
export type State = Schema.Schema.Type<typeof State>;

/** Rollup of every check run and commit status on the head commit. */
export const CheckState = Schema.Literals(['pending', 'success', 'failure', 'neutral']);
export type CheckState = Schema.Schema.Type<typeof CheckState>;

/** The verdict branch protection reads from the pull request's reviews. */
export const ReviewDecision = Schema.Literals(['approved', 'changes_requested', 'review_required']);
export type ReviewDecision = Schema.Schema.Type<typeof ReviewDecision>;

/**
 * A proposed change to a repository. Host-agnostic like `Repo` and `Issue`, and named by
 * `owner`/`repo`/`number` for the same reason: a preview built from a URL alone is complete.
 */
export class PullRequest extends Type.makeObject<PullRequest>(DXN.make('org.dxos.type.pullRequest', '0.1.0'))(
  Schema.Struct({
    /** Account the repository belongs to, e.g. `dxos`. */
    owner: Schema.String.pipe(
      Schema.annotate({ title: 'Owner' }),
      Annotation.GeneratorAnnotation.set('internet.username'),
    ),

    /** Repository name without its owner, e.g. `dxos`. */
    repo: Schema.String.pipe(
      Schema.annotate({ title: 'Repository' }),
      Annotation.GeneratorAnnotation.set('company.buzzNoun'),
    ),

    /** Number within the repository, shared with issues, e.g. `123` in `dxos/dxos#123`. */
    number: Schema.Number.pipe(Schema.annotate({ title: 'Number' })),

    title: Schema.String.pipe(
      Schema.annotate({ title: 'Title' }),
      Annotation.GeneratorAnnotation.set('lorem.sentence'),
    ),

    /** Canonical web URL — stored rather than derived, since only the host knows its URL shape. */
    url: Format.URL.pipe(Schema.annotate({ title: 'URL' }), Schema.optional),

    state: State.pipe(Schema.annotate({ title: 'State' })),

    /** Login of the account that opened it. */
    author: Schema.String.pipe(Schema.annotate({ title: 'Author' }), Schema.optional),

    description: Format.Text.pipe(Schema.annotate({ title: 'Description' }), Schema.optional),

    /** Branch the change is proposed against, e.g. `main`. */
    baseBranch: Schema.String.pipe(Schema.annotate({ title: 'Base branch' }), Schema.optional),

    /** Branch carrying the change. */
    headBranch: Schema.String.pipe(Schema.annotate({ title: 'Head branch' }), Schema.optional),

    additions: Schema.Number.pipe(Schema.annotate({ title: 'Additions' }), Schema.optional),

    deletions: Schema.Number.pipe(Schema.annotate({ title: 'Deletions' }), Schema.optional),

    /** When it was opened on the host; absent on a pull request stored before the host reported it. */
    createdAt: Schema.optional(Format.DateTime.annotate({ title: 'Created' })),

    /** When the host last saw activity on it — a push, a review, a comment. */
    updatedAt: Schema.optional(Format.DateTime.annotate({ title: 'Updated' })),

    /** The repository it belongs to; absent on a preview, set by sync and by import. `repo` stays the name string. */
    repository: Schema.optional(Ref.Ref(Repo.Repo).annotate({ title: 'Repository' })),

    /** Commit at the head branch's tip; {@link checks} describes this commit. */
    headSha: Schema.optional(Schema.String.annotate({ title: 'Head commit' })),

    /** Rollup of every check run and status on `headSha`; absent when the commit has none. */
    checks: Schema.optional(CheckState.annotate({ title: 'Checks' })),

    reviewDecision: Schema.optional(ReviewDecision.annotate({ title: 'Review' })),

    /** Issues the host closes when it merges. */
    closes: Schema.optional(
      Schema.Array(Schema.suspend((): Ref.RefSchema<Task.Task> => Ref.Ref(Task.Task))).annotate({ title: 'Closes' }),
    ),
  }).pipe(
    Schema.annotate({ title: 'Pull request', description: 'A proposed change to a repository.' }),
    Annotation.LabelAnnotation.set(['title']),
    Annotation.IconAnnotation.set({ icon: 'ph--git-pull-request--regular', hue: 'green' }),
    Annotation.UserType.set(),
  ),
) {}

/** Factory wrapper around `Obj.make` for {@link PullRequest}. */
export const make = (props: Obj.MakeProps<typeof PullRequest>): PullRequest => Obj.make(PullRequest, props);

/** Returns true when value is a PullRequest object. */
export const instanceOf = (value: unknown): value is PullRequest => Obj.instanceOf(PullRequest, value);

/** `owner/repo#123` — the same reference form as an issue, since the numbers share one sequence. */
export const reference = Issue.reference;
